// Mirror of server/src/services/issueClassifier.ts rule ladder.
// Keep in sync manually — any change to the backend classifier should be
// reflected here (label, class, criteria, confidence, notePattern).
//
// notePattern is how we infer which rule fired from the persisted
// classifier_notes text (until the backend persists the rule id directly).

export type ClassifierRule = {
  id: string;
  label: string;
  className: string;
  criteria: string;
  confidence: number;
  notePattern: RegExp;
};

export const CLASSIFIER_RULES: ClassifierRule[] = [
  {
    id: 'P1-FALSE-ALERT',
    label: 'Pass-through: child FALSE_ALERT',
    className: 'FALSE_ALERT',
    criteria: 'Any child alert already classified as FALSE_ALERT — trust the upstream classification.',
    confidence: 95,
    notePattern: /^Upstream FALSE_ALERT/,
  },
  {
    id: 'P2-EOL',
    label: 'Pass-through: upstream failure_mode = HARDWARE_FAULT_EOL',
    className: 'HARDWARE_FAULT_EOL',
    criteria: 'sensorflow_analytics.v_node_offline_investigation.failure_mode = HARDWARE_FAULT_EOL (analytics pre-classification).',
    confidence: 90,
    notePattern: /^sensorflow_analytics: failure_mode=HARDWARE_FAULT_EOL/,
  },
  {
    id: 'P3-HW-FAULT',
    label: 'Pass-through: upstream failure_mode = HARDWARE_FAULT',
    className: 'HARDWARE_FAULT',
    criteria: 'sensorflow_analytics.v_node_offline_investigation.failure_mode = HARDWARE_FAULT (analytics pre-classification).',
    confidence: 90,
    notePattern: /^sensorflow_analytics: failure_mode=HARDWARE_FAULT\b/,
  },
  {
    id: 'P4-BATTERY-REMOVED',
    label: 'Override: BATTERY_DEPLETED but action says battery removed → tampering',
    className: 'TAMPERING',
    criteria: "failure_mode=BATTERY_DEPLETED AND battery_suggested_action contains 'battery was removed' or 'replace node' (sudden dropout on healthy battery = tampering or hardware, not depletion).",
    confidence: 85,
    notePattern: /^sensorflow_analytics: BATTERY_DEPLETED but suggested action/,
  },
  {
    id: 'P4-BATTERY',
    label: 'Pass-through: upstream failure_mode = BATTERY_DEPLETED',
    className: 'BATTERY_DRAINED',
    criteria: 'sensorflow_analytics.v_node_offline_investigation.failure_mode = BATTERY_DEPLETED (analytics pre-classification).',
    confidence: 90,
    notePattern: /^sensorflow_analytics: failure_mode=BATTERY_DEPLETED/,
  },
  {
    id: 'P5-GATEWAY',
    label: 'Pass-through: sensorflow_analytics gateway offline',
    className: 'ON_SITE_INVESTIGATION',
    criteria: 'Gateway at this location is offline — check the gateway before the node.',
    confidence: 85,
    notePattern: /^Gateway at this location/,
  },
  {
    id: 'R-GW-DIRECT',
    label: 'R-GW-DIRECT',
    className: 'ON_SITE_INVESTIGATION',
    criteria: 'sensorflow.gateways.is_online = false (direct gateway probe, sensorflow_analytics may be missing).',
    confidence: 80,
    notePattern: /^Gateway offline — verify gateway power/,
  },
  {
    id: 'R-OTA-FAIL',
    label: 'R-OTA-FAIL',
    className: 'FIRMWARE_ISSUE',
    criteria: 'Last OTA result is fail/error/timeout within the offline window.',
    confidence: 80,
    notePattern: /^OTA .*→.* (failed|fail|error|timeout) \d+d ago/,
  },
  {
    id: 'R-OTA-WINDOW',
    label: 'R-OTA-WINDOW',
    className: 'FIRMWARE_ISSUE',
    criteria: 'A successful OTA completed within 7 days before the node went offline.',
    confidence: 75,
    notePattern: /^OTA .*→.* completed \d+d before offline/,
  },
  {
    id: 'R-BAT-CHANGE',
    label: 'R-BAT-CHANGE',
    className: 'BATTERY_DRAINED',
    criteria: 'battery_suggested_action starts with "Change batteries".',
    confidence: 80,
    notePattern: /^Change batteries/i,
  },
  {
    id: 'R-BAT-REPLACE-NODE',
    label: 'R-BAT-REPLACE-NODE',
    className: 'HARDWARE_FAULT',
    criteria: 'battery_suggested_action starts with "Replace node".',
    confidence: 80,
    notePattern: /^Battery analytics: likely hardware fault/,
  },
  {
    id: 'R-BAT-ONSITE',
    label: 'R-BAT-ONSITE',
    className: 'ON_SITE_INVESTIGATION',
    criteria: 'battery_suggested_action starts with "Investigate on site".',
    confidence: 70,
    notePattern: /^Battery removed or unknown/,
  },
  {
    id: 'R-TESTJIG',
    label: 'R-TESTJIG',
    className: 'HARDWARE_FAULT',
    criteria: 'Test jig report returned False within the last 14 days.',
    confidence: 90,
    notePattern: /^Test jig failure on/,
  },
  {
    id: 'R-REMAP',
    label: 'R-REMAP',
    className: 'OUT_OF_RANGE',
    criteria: 'Node re-mapped within 7 days before going offline — position likely bad.',
    confidence: 65,
    notePattern: /^Node re-mapped/,
  },
  {
    id: 'R-SUDDEN-WEAK',
    label: 'R-SUDDEN-WEAK',
    className: 'OUT_OF_RANGE',
    criteria: 'Sudden dropout pattern with avg signal below -85 dBm.',
    confidence: 55,
    notePattern: /^Sudden dropout/,
  },
  {
    id: 'R-WEAK-PERSISTENT',
    label: 'R-WEAK-PERSISTENT',
    className: 'OUT_OF_RANGE',
    criteria: 'Avg signal < -85 dBm without a sudden dropout (gradual range degradation).',
    confidence: 45,
    notePattern: /^Persistent weak signal/,
  },
  {
    id: 'R-REPEAT',
    label: 'R-REPEAT',
    className: 'HARDWARE_FAULT',
    criteria: '≥3 offline events with the last < 30 days ago. Confidence 60/70/80 by event count.',
    confidence: 60,
    notePattern: /^Repeat offender:/,
  },
  {
    id: 'R-COHORT-GATEWAY',
    label: 'R-COHORT-GATEWAY',
    className: 'ON_SITE_INVESTIGATION',
    criteria: '≥5 other nodes on the same gateway are also offline within 24h.',
    confidence: 70,
    notePattern: /^Gateway cohort:/,
  },
  {
    id: 'R-COHORT',
    label: 'R-COHORT',
    className: 'OFFLINE_UNKNOWN',
    criteria: '≥5 nodes at the same location went offline within 24h (batch dropout).',
    confidence: 50,
    notePattern: /^Part of batch dropout/,
  },
  {
    id: 'R-ALERT-HARDWARE',
    label: 'R-ALERT-HARDWARE',
    className: 'HARDWARE_FAULT',
    criteria: 'Child alert classified as HARDWARE_FAULT and no stronger signal fired.',
    confidence: 55,
    notePattern: /^Upstream HARDWARE_FAULT/,
  },
  {
    id: 'R-ALERT-FIRMWARE',
    label: 'R-ALERT-FIRMWARE',
    className: 'FIRMWARE_ISSUE',
    criteria: 'Child alert classified as FIRMWARE_ISSUE and no stronger signal fired.',
    confidence: 55,
    notePattern: /^Upstream FIRMWARE_ISSUE/,
  },
  {
    id: 'R-ALERT-OOR',
    label: 'R-ALERT-OOR',
    className: 'OUT_OF_RANGE',
    criteria: 'Child alert classified as OUT_OF_RANGE and no stronger signal fired.',
    confidence: 55,
    notePattern: /^Upstream OUT_OF_RANGE/,
  },
  {
    id: 'R-ALERT-BATTERY',
    label: 'R-ALERT-BATTERY',
    className: 'BATTERY_DRAINED',
    criteria: 'Child alert classified as BATTERY_DRAINED and no stronger signal fired.',
    confidence: 55,
    notePattern: /^Upstream BATTERY_DRAINED/,
  },
  {
    id: 'R-FALLBACK-STALE',
    label: 'R-FALLBACK-STALE',
    className: 'OFFLINE_UNKNOWN',
    criteria: 'Node silent ≥30 days but no other rule fired — bumped over the cold fallback.',
    confidence: 35,
    notePattern: /^Stale \d+d, no recovery/,
  },
  {
    id: 'R-FALLBACK',
    label: 'R-FALLBACK',
    className: 'OFFLINE_UNKNOWN',
    criteria: 'No rule matched — defaulted to offline-unknown, needs manual review.',
    confidence: 20,
    notePattern: /^No strong signal/,
  },
];

export function matchRule(notes: string | null | undefined): ClassifierRule | null {
  if (!notes) return null;
  const stripped = notes.replace(/^\(pinned\) /, '');
  for (const rule of CLASSIFIER_RULES) {
    if (rule.notePattern.test(stripped)) return rule;
  }
  return null;
}

// One-line recommended next step keyed by the class. Driven by class because
// most actionable handling is class-level (battery → schedule swap; HW fault
// → ship for repair). Can refine per-rule later if any rule needs a more
// specific recipe than its class implies.
const NEXT_STEP_BY_CLASS: Record<string, string> = {
  FALSE_ALERT: 'Close the issue — no real action needed.',
  BATTERY_DRAINED: 'Schedule a battery change at the room. Confirm during the visit.',
  HARDWARE_FAULT: 'Send the node for repair or replace it.',
  HARDWARE_FAULT_EOL: 'Replace the node — past end of life.',
  TAMPERING: 'Investigate physical tampering on-site; verify the node is in place.',
  FIRMWARE_ISSUE: 'Hold and watch the next OTA cycle. Escalate if it persists after the next firmware push.',
  OUT_OF_RANGE: 'Re-position the node or check gateway placement / line of sight.',
  ON_SITE_INVESTIGATION: 'Send a tech on-site — cause not determinable remotely.',
  OFFLINE_UNKNOWN: 'Needs manual triage; the rule chain found no strong signal. Open the detail panel and look at recent alerts + battery + signal sparkline.',
};

export function getNextStep(className: string | null | undefined): string | null {
  if (!className) return null;
  return NEXT_STEP_BY_CLASS[className.toUpperCase()] ?? null;
}
